import React, { useState, useEffect } from 'react';
import { saveUserTemplate, deleteUserTemplate, saveSystemTemplate, deleteSystemTemplate, uploadTemplateBg, deleteTemplateBg } from '../services/templateService';

// Special dropdown values
const AUTO_OPTION = 'Auto (AI decide)';
const CUSTOM_OPTION = 'Custom...';

/**
 * Edit / Create modal for a GOYANG template.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - onSaved: (templateId) => void
 *   - moduleId: string  (e.g. 'goyang')
 *   - hookPresets: Array<{id, name}>  — fragment objects (id = stable UUID, name = display)
 *   - bgPresets: Array<{id, name}>
 *   - template: object | null  (null = create new; object = edit existing)
 */
export default function TemplateEditorModal({ open, onClose, onSaved, moduleId, hookPresets = [], bgPresets = [], template = null, forceSystem = false }) {
    // forceSystem: admin mode — save to system_templates instead of users/.
    // When forceSystem=true, isSystem template is editable (not cloned).
    const isClone = !!(template && template.isSystem && !forceSystem);
    const isEditSystem = !!(template && template.isSystem && forceSystem);
    const isEdit = !!(template && (isEditSystem || !template.isSystem));
    const [name, setName] = useState('');
    // Set shape: { hookId, bgId, hook (denormalized name cache), bg, hookCustom, bgCustom }
    // hookId/bgId = '' bila Auto/Custom; fragment UUID bila pilih dari dropdown.
    const [sets, setSets] = useState({
        set1: { hookId: '', bgId: '', hook: AUTO_OPTION, bg: AUTO_OPTION, hookCustom: '', bgCustom: '' },
        set2: { hookId: '', bgId: '', hook: AUTO_OPTION, bg: AUTO_OPTION, hookCustom: '', bgCustom: '' },
        set3: { hookId: '', bgId: '', hook: AUTO_OPTION, bg: AUTO_OPTION, hookCustom: '', bgCustom: '' }
    });
    const [customBgImageUrl, setCustomBgImageUrl] = useState('');
    const [customBgFilename, setCustomBgFilename] = useState('');
    const [handGender, setHandGender] = useState('auto');  // 'auto' | 'female' | 'male'
    const [pendingUploadUrl, setPendingUploadUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        if (template) {
            setName(isClone ? `${template.name || ''} (Copy)` : (template.name || ''));
            const hydrate = (s) => ({
                hookId: s?.hookId || '',
                bgId: s?.bgId || '',
                hook: s?.hook || AUTO_OPTION,
                bg: s?.bg || AUTO_OPTION,
                hookCustom: s?.hookCustom || '',
                bgCustom: s?.bgCustom || ''
            });
            setSets({
                set1: hydrate(template.set1),
                set2: hydrate(template.set2),
                set3: hydrate(template.set3)
            });
        } else {
            setName('');
            setSets({
                set1: { hookId: '', bgId: '', hook: AUTO_OPTION, bg: AUTO_OPTION, hookCustom: '', bgCustom: '' },
                set2: { hookId: '', bgId: '', hook: AUTO_OPTION, bg: AUTO_OPTION, hookCustom: '', bgCustom: '' },
                set3: { hookId: '', bgId: '', hook: AUTO_OPTION, bg: AUTO_OPTION, hookCustom: '', bgCustom: '' }
            });
        }
        // Hydrate bg fields. Clone starts CLEAN (don't copy other user's storage URL — owner rules).
        if (template && !isClone) {
            setCustomBgImageUrl(template.customBgImageUrl || '');
            setCustomBgFilename(template.customBgFilename || '');
        } else {
            setCustomBgImageUrl('');
            setCustomBgFilename('');
        }
        // Hand gender — hydrate from template (preserve through clone)
        setHandGender(template?.handGender || 'auto');
        setPendingUploadUrl(null);
        setUploading(false);
        setError('');
    }, [open, template, isClone]);

    if (!open) return null;

    const updateSet = (sid, field, value) => {
        setSets(prev => ({ ...prev, [sid]: { ...prev[sid], [field]: value } }));
    };
    const updateSetFields = (sid, fields) => {
        setSets(prev => ({ ...prev, [sid]: { ...prev[sid], ...fields } }));
    };
    // Sentinel values untuk dropdown — bukan UUID, jadi tak collide.
    const SENTINEL_AUTO = '__auto__';
    const SENTINEL_CUSTOM = '__custom__';

    const handleBgUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setError('');
        try {
            const tplId = template?.id || null;
            const { url, filename } = await uploadTemplateBg(moduleId, tplId, file);
            if (pendingUploadUrl) await deleteTemplateBg(pendingUploadUrl);
            setPendingUploadUrl(url);
            setCustomBgImageUrl(url);
            setCustomBgFilename(filename);
        } catch (err) {
            console.error('[TemplateEditor] Bg upload error:', err);
            setError(err.message || 'Gagal upload image.');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleBgRemove = async () => {
        if (pendingUploadUrl && customBgImageUrl === pendingUploadUrl) {
            await deleteTemplateBg(pendingUploadUrl);
            setPendingUploadUrl(null);
        }
        setCustomBgImageUrl('');
        setCustomBgFilename('');
    };

    const handleCancel = async () => {
        if (pendingUploadUrl) {
            await deleteTemplateBg(pendingUploadUrl);
            setPendingUploadUrl(null);
        }
        onClose?.();
    };

    const handleSave = async () => {
        if (!name.trim()) {
            setError('Nama template wajib.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            // Pick save function based on mode
            const saveFn = forceSystem ? saveSystemTemplate : saveUserTemplate;
            let id;
            if (isClone) {
                // Clone (only happens when forceSystem=false + template is system) — write as new user template
                id = await saveUserTemplate(moduleId, {
                    name: name.trim(),
                    description: template?.description || '',
                    customBgImageUrl,
                    customBgFilename,
                    handGender,
                    set1: sets.set1,
                    set2: sets.set2,
                    set3: sets.set3
                });
            } else if (isEdit) {
                id = await saveFn(moduleId, {
                    id: template.id,
                    name: name.trim(),
                    description: template?.description || '',
                    customBgImageUrl,
                    customBgFilename,
                    handGender,
                    set1: sets.set1,
                    set2: sets.set2,
                    set3: sets.set3
                });
            } else {
                id = await saveFn(moduleId, {
                    name: name.trim(),
                    customBgImageUrl,
                    customBgFilename,
                    handGender,
                    set1: sets.set1,
                    set2: sets.set2,
                    set3: sets.set3
                });
            }
            setPendingUploadUrl(null);  // committed via save
            onSaved?.(id);
            onClose?.();
        } catch (err) {
            console.error('[TemplateEditor] Save error:', err);
            setError(err.message || 'Gagal simpan.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!isEdit) return;
        if (!confirm(`Padam template "${template.name}"? Tindakan tak boleh undo.`)) return;
        setSaving(true);
        try {
            const deleteFn = forceSystem ? deleteSystemTemplate : deleteUserTemplate;
            await deleteFn(moduleId, template.id);
            onSaved?.(null);
            onClose?.();
        } catch (err) {
            console.error('[TemplateEditor] Delete error:', err);
            setError(err.message || 'Gagal padam.');
            setSaving(false);
        }
    };

    const renderSetCard = (sid, label) => {
        const s = sets[sid];
        // Dropdown value = hookId (kalau pilih fragment) atau sentinel (kalau Auto/Custom).
        const hookValue = s.hookId || (s.hook === CUSTOM_OPTION ? SENTINEL_CUSTOM : SENTINEL_AUTO);
        const bgValue = s.bgId || (s.bg === CUSTOM_OPTION ? SENTINEL_CUSTOM : SENTINEL_AUTO);
        const onHookChange = (val) => {
            if (val === SENTINEL_AUTO) {
                updateSetFields(sid, { hookId: '', hook: AUTO_OPTION });
            } else if (val === SENTINEL_CUSTOM) {
                updateSetFields(sid, { hookId: '', hook: CUSTOM_OPTION });
            } else {
                const frag = hookPresets.find(f => f.id === val);
                updateSetFields(sid, { hookId: val, hook: frag?.name || '' });
            }
        };
        const onBgChange = (val) => {
            if (val === SENTINEL_AUTO) {
                updateSetFields(sid, { bgId: '', bg: AUTO_OPTION });
            } else if (val === SENTINEL_CUSTOM) {
                updateSetFields(sid, { bgId: '', bg: CUSTOM_OPTION });
            } else {
                const frag = bgPresets.find(f => f.id === val);
                updateSetFields(sid, { bgId: val, bg: frag?.name || '' });
            }
        };
        return (
            <div key={sid} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#6366f1', marginBottom: 8, letterSpacing: '0.04em' }}>{label}</div>

                <label style={{ display: 'block', fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Hook</label>
                <select value={hookValue} onChange={e => onHookChange(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', fontSize: 11, fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: 6, background: '#ffffff', marginBottom: 6 }}>
                    <option value={SENTINEL_AUTO}>{AUTO_OPTION}</option>
                    {hookPresets.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    {forceSystem && <option value={SENTINEL_CUSTOM}>{CUSTOM_OPTION}</option>}
                </select>
                {s.hook === CUSTOM_OPTION && (
                    <input type="text" value={s.hookCustom} onChange={e => updateSet(sid, 'hookCustom', e.target.value)}
                        placeholder="Custom hook style — taip sendiri..."
                        style={{ width: '100%', padding: '6px 8px', fontSize: 11, border: '1px solid #6366f1', borderRadius: 6, marginBottom: 6, boxSizing: 'border-box' }} />
                )}

                <div style={{ opacity: customBgImageUrl ? 0.4 : 1, pointerEvents: customBgImageUrl ? 'none' : 'auto' }}>
                    <label style={{ display: 'block', fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4, marginTop: 4 }}>Background</label>
                    <select value={bgValue} onChange={e => onBgChange(e.target.value)}
                        style={{ width: '100%', padding: '6px 8px', fontSize: 11, fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: 6, background: '#ffffff' }}>
                        <option value={SENTINEL_AUTO}>{AUTO_OPTION}</option>
                        {bgPresets.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        {forceSystem && <option value={SENTINEL_CUSTOM}>{CUSTOM_OPTION}</option>}
                    </select>
                    {s.bg === CUSTOM_OPTION && (
                        <input type="text" value={s.bgCustom} onChange={e => updateSet(sid, 'bgCustom', e.target.value)}
                            placeholder="Custom background — taip sendiri..."
                            style={{ width: '100%', padding: '6px 8px', fontSize: 11, border: '1px solid #6366f1', borderRadius: 6, marginTop: 6, boxSizing: 'border-box' }} />
                    )}
                </div>
                {customBgImageUrl && (
                    <div style={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic', marginTop: 4 }}>
                        ⓘ Override aktif — bg per-set di-ignore.
                    </div>
                )}
            </div>
        );
    };

    const titleText = isClone
        ? 'Clone Template'
        : (forceSystem
            ? (isEdit ? 'Edit System Template' : 'System Template Baru')
            : (isEdit ? 'Edit Template' : 'Template Baru'));
    const subtitleText = forceSystem
        ? 'GOYANG — Admin: System template (semua user nampak)'
        : 'GOYANG — Hook + Background per set';
    const saveButtonText = saving
        ? 'Saving...'
        : (isClone ? 'Save as Copy' : (forceSystem ? 'Simpan System' : 'Simpan'));

    return (
        <div onClick={handleCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#ffffff', borderRadius: 14, padding: 20, width: 460, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#8b5cf6' }}>palette</span>
                    </div>
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>{titleText}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>{subtitleText}</div>
                    </div>
                </div>

                {isClone && (
                    <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 10, color: '#92400e' }}>
                        💡 Template asal di-lock (system template). Save akan buat copy baru dalam library kau.
                    </div>
                )}

                <label style={{ display: 'block', fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Nama Template</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Contoh: Skincare Lifestyle"
                    style={{ width: '100%', padding: '8px 10px', fontSize: 12, fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 14, boxSizing: 'border-box' }} />

                {/* Custom BG Image — optional, applied to all 3 sets as Flow reference */}
                <label style={{ display: 'block', fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>🖼️ Custom Background (Optional)</label>
                <div style={{ marginBottom: 14, padding: 10, background: '#fafbfc', border: '1px dashed #cbd5e1', borderRadius: 8 }}>
                    {customBgImageUrl ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <img src={customBgImageUrl} alt="bg preview" style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {customBgFilename || 'background.jpg'}
                                </div>
                                <div style={{ fontSize: 9, color: '#10b981', fontWeight: 600, marginTop: 2 }}>
                                    ✓ Semua 3 set guna image ni sebagai reference
                                </div>
                            </div>
                            <label style={{ padding: '5px 10px', fontSize: 10, fontWeight: 700, color: '#6366f1', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, cursor: uploading ? 'wait' : 'pointer' }}>
                                Replace
                                <input type="file" accept="image/*" onChange={handleBgUpload} disabled={uploading} style={{ display: 'none' }} />
                            </label>
                            <button onClick={handleBgRemove} disabled={uploading} style={{ padding: '5px 10px', fontSize: 10, fontWeight: 700, color: '#dc2626', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, cursor: uploading ? 'wait' : 'pointer' }}>
                                Remove
                            </button>
                        </div>
                    ) : (
                        <label style={{ display: 'block', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer', padding: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', marginBottom: 4 }}>
                                {uploading ? '⏳ Uploading...' : '+ Upload Background Image'}
                            </div>
                            <div style={{ fontSize: 9, color: '#94a3b8' }}>
                                JPG / PNG / WebP — max 5MB. Apply ke semua 3 set.
                            </div>
                            <input type="file" accept="image/*" onChange={handleBgUpload} disabled={uploading} style={{ display: 'none' }} />
                        </label>
                    )}
                </div>

                {/* Hand Gender — GOYANG hand-only mode, force or auto */}
                <label style={{ display: 'block', fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>👋 Tangan (Hand Gender)</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                    {[
                        { value: 'auto', label: 'Auto', sub: 'AI decide ikut target market', emoji: '🤖' },
                        { value: 'female', label: 'Perempuan', sub: 'Force tangan + voiceover wanita', emoji: '👩' },
                        { value: 'male', label: 'Lelaki', sub: 'Force tangan + voiceover lelaki', emoji: '👨' }
                    ].map(opt => {
                        const active = handGender === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setHandGender(opt.value)}
                                title={opt.sub}
                                style={{
                                    flex: 1,
                                    padding: '8px 6px',
                                    background: active ? '#fef3c7' : '#ffffff',
                                    border: `1.5px solid ${active ? '#f59e0b' : '#e2e8f0'}`,
                                    borderRadius: 8,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 3,
                                    transition: 'all 0.15s'
                                }}
                            >
                                <span style={{ fontSize: 18 }}>{opt.emoji}</span>
                                <span style={{ fontSize: 10, fontWeight: 800, color: active ? '#b45309' : '#475569' }}>{opt.label}</span>
                            </button>
                        );
                    })}
                </div>

                {renderSetCard('set1', 'SET 1')}
                {renderSetCard('set2', 'SET 2')}
                {renderSetCard('set3', 'SET 3')}

                {error && (
                    <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 6, padding: 8, fontSize: 11, marginBottom: 10 }}>
                        ⚠ {error}
                    </div>
                )}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 14 }}>
                    <div>
                        {isEdit && (
                            <button onClick={handleDelete} disabled={saving}
                                style={{ padding: '8px 14px', fontSize: 11, fontWeight: 700, color: '#dc2626', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer' }}>
                                🗑 Padam
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={handleCancel} disabled={saving || uploading}
                            style={{ padding: '8px 14px', fontSize: 11, fontWeight: 600, color: '#64748b', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: (saving || uploading) ? 'not-allowed' : 'pointer' }}>
                            Batal
                        </button>
                        <button onClick={handleSave} disabled={saving || uploading}
                            style={{ padding: '8px 16px', fontSize: 11, fontWeight: 800, color: '#ffffff', background: forceSystem ? '#0891b2' : '#8b5cf6', border: 'none', borderRadius: 8, cursor: (saving || uploading) ? 'not-allowed' : 'pointer', boxShadow: `0 2px 8px ${forceSystem ? 'rgba(8,145,178,0.3)' : 'rgba(139,92,246,0.3)'}` }}>
                            {saveButtonText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
